import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Shield, Lock, Smartphone, CheckCircle, AlertTriangle, ArrowLeft, Copy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const TwoFactorSetupPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [enabled, setEnabled] = useState(false);
    const [secretData, setSecretData] = useState(null);
    const [token, setToken] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [backupCodes, setBackupCodes] = useState([]);

    useEffect(() => {
        checkStatus();
    }, []);

    const checkStatus = async () => {
        try {
            const res = await api.get('/auth/2fa/status');
            setEnabled(res.data.enabled);
            if (!res.data.enabled) {
                // Generate secret immediately if not enabled
                generateSecret();
            }
        } catch (error) {
            console.error('Failed to check 2FA status', error);
        } finally {
            setLoading(false);
        }
    };

    const generateSecret = async () => {
        try {
            const res = await api.post('/auth/2fa/generate');
            setSecretData(res.data);
        } catch (error) {
            toast.error('Failed to generate 2FA secret');
        }
    };

    const handleEnable = async (e) => {
        e.preventDefault();
        if (!token || !secretData) return;

        setVerifying(true);
        try {
            const res = await api.post('/auth/2fa/enable', {
                secret: secretData.secret,
                token: token
            });

            setEnabled(true);
            setBackupCodes(res.data.backupCodes || []);
            toast.success('2FA Enabled Successfully!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Invalid verification code');
        } finally {
            setVerifying(false);
        }
    };

    const handleDisable = async (e) => {
        e.preventDefault();
        if (!token) return;

        if (!window.confirm('Are you sure you want to disable 2FA? Your account will be less secure.')) return;

        setVerifying(true);
        try {
            await api.post('/auth/2fa/disable', { token });
            setEnabled(false);
            setSecretData(null);
            setToken('');
            setBackupCodes([]);
            toast.success('2FA Disabled');
            // Re-generate secret for next setup
            generateSecret();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Invalid verification code');
        } finally {
            setVerifying(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    return (
        <div className="bg-[#0b0e11] min-h-screen text-gray-100 font-sans selection:bg-blue-500/30">
            <Navbar />

            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="mb-8">
                    <Link to="/profile" className="text-gray-400 hover:text-white flex items-center gap-2 mb-4 transition">
                        <ArrowLeft size={20} /> Back to Profile
                    </Link>
                    <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                        <Shield className="text-blue-500" size={32} />
                        Two-Factor Authentication (2FA)
                    </h1>
                    <p className="text-gray-400 mt-2">Protect your account with an extra layer of security.</p>
                </div>

                {loading ? (
                    <div className="text-center py-12">Loading...</div>
                ) : enabled && backupCodes.length === 0 ? (
                    // 2FA Enabled State
                    <div className="bg-[#181a20] rounded-3xl border border-green-500/30 p-8 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-10">
                            <Shield size={120} />
                        </div>

                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center text-green-500">
                                <CheckCircle size={32} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">2FA is Enabled</h2>
                                <p className="text-green-400">Your account is secure.</p>
                            </div>
                        </div>

                        <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-700 mb-8">
                            <h3 className="text-white font-bold mb-2">Disable 2FA</h3>
                            <p className="text-gray-400 text-sm mb-4">
                                To disable 2FA, please enter, the code from your authenticator app.
                            </p>
                            <form onSubmit={handleDisable} className="flex gap-4">
                                <input
                                    type="text"
                                    placeholder="Enter 6-digit code"
                                    value={token}
                                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="flex-1 bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none transition font-mono text-center tracking-widest text-lg"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={verifying || token.length !== 6}
                                    className="bg-red-600 hover:bg-red-500 text-white font-bold px-6 py-3 rounded-xl transition disabled:opacity-50"
                                >
                                    Disable
                                </button>
                            </form>
                        </div>
                    </div>
                ) : backupCodes.length > 0 ? (
                    // Backup Codes State (Just Enabled)
                    <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 shadow-2xl">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center text-green-500 mx-auto mb-4">
                                <CheckCircle size={32} />
                            </div>
                            <h2 className="text-2xl font-bold text-white">2FA Enabled Successfully!</h2>
                            <p className="text-gray-400 mt-2">
                                Please save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
                            </p>
                        </div>

                        <div className="bg-[#0b0e11] p-6 rounded-2xl border border-gray-700 mb-8">
                            <div className="grid grid-cols-2 gap-4">
                                {backupCodes.map((code, index) => (
                                    <div key={index} className="font-mono text-center text-white bg-gray-800 p-2 rounded">
                                        {code}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-center">
                            <button
                                onClick={() => setBackupCodes([])}
                                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-3 rounded-xl transition"
                            >
                                I have saved these codes
                            </button>
                        </div>
                    </div>
                ) : (
                    // Setup State
                    <div className="bg-[#181a20] rounded-3xl border border-gray-800 p-8 shadow-2xl">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-500 font-bold text-xl">
                                1
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Download Authenticator App</h3>
                                <p className="text-gray-400 text-sm">Download Google Authenticator or Authy.</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-500 font-bold text-xl">
                                2
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-white">Scan QR Code</h3>
                                <p className="text-gray-400 text-sm mb-4">Open the app and scan this QR code.</p>

                                {secretData ? (
                                    <div className="flex flex-col md:flex-row gap-8 items-center bg-[#0b0e11] p-6 rounded-2xl border border-gray-700">
                                        <div className="bg-white p-2 rounded-lg">
                                            <QRCodeSVG value={secretData.qrCode} size={150} />
                                        </div>
                                        <div className="flex-1 w-full">
                                            <p className="text-sm text-gray-500 mb-1">If you can't scan the code, enter this secret key manually:</p>
                                            <div className="flex items-center gap-2 bg-[#181a20] p-3 rounded-xl border border-gray-700 group hover:border-blue-500/50 transition">
                                                <code className="flex-1 text-blue-400 font-mono tracking-wider break-all">
                                                    {secretData.secret}
                                                </code>
                                                <button
                                                    onClick={() => copyToClipboard(secretData.secret)}
                                                    className="p-2 text-gray-400 hover:text-white rounded-lg transition"
                                                >
                                                    <Copy size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="animate-pulse bg-gray-800 h-48 rounded-xl"></div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center text-blue-500 font-bold text-xl">
                                3
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-white">Enter Verification Code</h3>
                                <p className="text-gray-400 text-sm">Enter the 6-digit code from your app.</p>
                            </div>
                        </div>

                        <form onSubmit={handleEnable} className="pl-16">
                            <div className="flex gap-4 max-w-sm">
                                <input
                                    type="text"
                                    placeholder="000 000"
                                    value={token}
                                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="flex-1 bg-[#0b0e11] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none transition font-mono text-center tracking-widest text-lg"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={verifying || token.length !== 6 || !secretData}
                                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-3 rounded-xl transition disabled:opacity-50"
                                >
                                    {verifying ? 'Verifying...' : 'Enable 2FA'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TwoFactorSetupPage;
